import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, Truck, X } from 'lucide-react';
import { instances, servers, type AuftragAngelegt } from '../api';
import type { Instance } from '../types';

/**
 * Eine Instanz mitsamt ihren Daten auf einen anderen Server ziehen.
 *
 * Die Vorprüfung ist hier nicht Beiwerk, sondern der eigentliche Inhalt. Ab dem
 * Anstoßen steht die Instanz still, und was danach schiefgeht, geht mitten im
 * Umzug schief. Deshalb: erst zeigen, was übertragen wird und was dagegen
 * spricht, dann anstoßen.
 *
 * Besonders `lesbar`. Ein Bind-Mount, an den der SSH-Benutzer nicht herankommt,
 * misst sich als 0 Byte — und ein `tar` darauf erzeugt ein leeres Archiv und
 * meldet Erfolg. Unlesbar ist etwas anderes als leer, und das muss man sehen,
 * bevor man klickt.
 */
/** Bytes lesbar. Der Plan liefert rohe Zahlen, kein fertiges Format. */
function bytesLesbar(n: number | null): string {
  if (n === null) return '?';
  const e = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let w = n;
  while (w >= 1024 && i < e.length - 1) {
    w /= 1024;
    i++;
  }
  return `${w < 10 && i > 0 ? w.toFixed(1) : Math.round(w)} ${e[i]}`;
}

export default function UmzugModal({
  instanz,
  onSchliessen,
  onEingereiht,
}: {
  instanz: Instance;
  onSchliessen: () => void;
  onEingereiht: (auftrag: AuftragAngelegt) => void;
}) {
  const { t } = useTranslation();
  const [ziel, setZiel] = useState('');
  const [fehler, setFehler] = useState('');

  const serverListe = useQuery({
    queryKey: ['servers'],
    queryFn: () => servers.list(),
  });

  const plan = useQuery({
    queryKey: ['instance-migration', instanz.id, ziel],
    queryFn: () => instances.migrationPlan(instanz.id, ziel),
    enabled: Boolean(ziel),
  });

  const umziehen = useMutation({
    mutationFn: () => instances.migrate(instanz.id, ziel),
    onSuccess: onEingereiht,
    onError: (err: unknown) =>
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          t('migrate.failed'),
      ),
  });

  const kandidaten = (serverListe.data ?? []).filter((s) => s.id !== instanz.serverId);
  const daten = plan.data;
  const unlesbar = (daten?.items ?? []).filter((i) => !i.lesbar);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-surface shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-start gap-2">
            <Truck size={18} className="mt-0.5 shrink-0 text-slate-500" />
            <div>
              <h2 className="text-base font-semibold text-slate-800">{t('migrate.title')}</h2>
              <p className="mt-0.5 text-xs text-slate-500">{t('migrate.subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onSchliessen}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
              {instanz.server?.hostname ?? t('migrate.noSource')}
            </span>
            <ArrowRight size={14} className="text-slate-400" />
            <select
              value={ziel}
              onChange={(e) => {
                setZiel(e.target.value);
                setFehler('');
              }}
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            >
              <option value="">{t('migrate.pickTarget')}</option>
              {kandidaten.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.hostname}
                </option>
              ))}
            </select>
          </div>

          {plan.isFetching && <div className="text-xs text-slate-500">{t('migrate.checking')}</div>}

          {daten && (
            <>
              <div>
                <div className="mb-1 text-xs font-medium text-slate-600">
                  {t('migrate.dataTitle', {
                    count: daten.items.length,
                    size: bytesLesbar(daten.totalBytes),
                  })}
                </div>
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {daten.items.map((i) => (
                    <div key={i.name} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <span className="w-12 shrink-0 text-slate-400">{i.kind}</span>
                      <span className="min-w-0 flex-1 truncate font-mono text-slate-700">
                        {i.name}
                      </span>
                      <span className="shrink-0 tabular-nums text-slate-500">
                        {i.lesbar ? bytesLesbar(i.bytes) : t('migrate.unreadable')}
                      </span>
                    </div>
                  ))}
                  {!daten.items.length && (
                    <div className="px-3 py-2 text-xs text-slate-500">{t('migrate.noData')}</div>
                  )}
                </div>
              </div>

              {/* Unlesbar ist kein Schoenheitsfehler -- daraus wird ein leeres
                  Archiv, das Erfolg meldet. */}
              {unlesbar.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>{t('migrate.unreadableWarning', { count: unlesbar.length })}</span>
                </div>
              )}

              <div>
                <div className="mb-1 text-xs font-medium text-slate-600">
                  {t('migrate.checksTitle')}
                </div>
                <div className="space-y-1">
                  {daten.checks.map((c) => (
                    <div key={c.key} className="flex items-start gap-2 text-xs">
                      {c.status === 'PASS' ? (
                        <Check size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                      ) : (
                        <AlertTriangle
                          size={13}
                          className={`mt-0.5 shrink-0 ${
                            c.status === 'FAIL' && c.blocking ? 'text-red-600' : 'text-amber-500'
                          }`}
                        />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="text-slate-700">{c.label}</span>
                        {c.value && <span className="ml-1 text-slate-500">— {c.value}</span>}
                        {c.detail && (
                          <span className="mt-0.5 block text-slate-400">{c.detail}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                {t('migrate.whatHappens')}
              </div>
            </>
          )}

          {fehler && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{fehler}</div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-4">
          <span className="text-xs text-slate-400">
            {daten && !daten.ok ? t('migrate.blocked') : ''}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSchliessen}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={!daten?.ok || umziehen.isPending}
              onClick={() => umziehen.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
            >
              <Truck size={14} />
              {umziehen.isPending ? t('migrate.running') : t('migrate.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
